<template>
    <div class="menu-page">
        <el-card shadow="never">
            <template #header>
                <div class="page-header">
                    <div>
                        <div class="page-title">菜单管理</div>
                        <div class="page-subtitle">配置后台侧边栏菜单，报表菜单填写积木报表 ID 即可挂载</div>
                    </div>
                    <el-button type="primary" @click="openAdd(rootMenu)">新增一级菜单</el-button>
                </div>
            </template>

            <el-table :data="menus" row-key="id" border default-expand-all>
                <el-table-column prop="title" label="菜单名称" min-width="180" />
                <el-table-column prop="path" label="路由地址" min-width="220" />
                <el-table-column prop="menuType" label="类型" width="100">
                    <template #default="{ row }">
                        <el-tag :type="menuTypeTag(row.menuType)">{{ menuTypeText(row.menuType) }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="reportId" label="报表ID" min-width="180" />
                <el-table-column prop="icon" label="图标" width="120" />
                <el-table-column prop="sortOrder" label="排序" width="80" />
                <el-table-column prop="visible" label="显示" width="80">
                    <template #default="{ row }">
                        <el-tag :type="row.visible === 1 ? 'success' : 'info'">{{ row.visible === 1 ? '显示' : '隐藏' }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="status" label="状态" width="80">
                    <template #default="{ row }">
                        <el-tag :type="row.status === 1 ? 'success' : 'danger'">{{ row.status === 1 ? '启用' : '停用' }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="250" fixed="right">
                    <template #default="{ row }">
                        <el-button link type="primary" @click="openAdd(row)">新增子级</el-button>
                        <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
                        <el-button link :type="row.status === 1 ? 'warning' : 'success'" @click="toggleStatus(row)">
                            {{ row.status === 1 ? '停用' : '启用' }}
                        </el-button>
                        <el-button link type="danger" @click="removeMenu(row)">删除</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <el-dialog v-model="dialogVisible" :title="form.id ? '编辑菜单' : '新增菜单'" width="640px">
            <el-form :model="form" label-width="96px">
                <el-form-item label="上级菜单">
                    <el-tree-select
                        v-model="form.parentId"
                        :data="menuOptions"
                        check-strictly
                        default-expand-all
                        node-key="id"
                        :props="{ label: 'title', value: 'id', children: 'children' }" />
                </el-form-item>
                <el-form-item label="菜单名称">
                    <el-input v-model="form.title" placeholder="例如：对账报表" />
                </el-form-item>
                <el-form-item label="菜单类型">
                    <el-select v-model="form.menuType">
                        <el-option label="目录" value="catalog" />
                        <el-option label="页面" value="page" />
                        <el-option label="积木报表" value="report" />
                    </el-select>
                </el-form-item>
                <el-form-item label="路由地址">
                    <el-input v-model="form.path" placeholder="例如：/reportManage/view/报表ID" />
                </el-form-item>
                <el-form-item label="组件标识">
                    <el-input v-model="form.component" placeholder="例如：ReportManage，可为空" />
                </el-form-item>
                <el-form-item label="图标名称">
                    <el-input v-model="form.icon" placeholder="例如：Reading、Goods、Connection" />
                </el-form-item>
                <el-form-item label="报表ID" v-if="form.menuType === 'report'">
                    <el-input v-model="form.reportId" placeholder="积木报表 ID" @change="syncReportPath" />
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="form.sortOrder" :min="0" />
                </el-form-item>
                <el-form-item label="显示/状态">
                    <el-switch v-model="form.visible" :active-value="1" :inactive-value="0" active-text="显示" inactive-text="隐藏" />
                    <el-switch class="status-switch" v-model="form.status" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="停用" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitForm">保存</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { deleteMenuFun, getMenuTreeFun, saveMenuFun, updateMenuStatusFun } from "@/api/menu";

const rootMenu = { id: 0, title: "顶级菜单", children: [] };
const menus = ref([]);
const dialogVisible = ref(false);
const form = reactive(defaultForm());
const menuOptions = computed(() => [rootMenu, ...menus.value]);

function defaultForm() {
    return {
        id: null,
        parentId: 0,
        title: "",
        path: "",
        component: "",
        icon: "",
        menuType: "page",
        reportId: "",
        sortOrder: 0,
        visible: 1,
        status: 1,
    };
}

function resetForm(data = {}) {
    Object.assign(form, defaultForm(), data);
}

function loadMenus() {
    getMenuTreeFun().then((res) => {
        if (res.code === 200) {
            menus.value = res.data || [];
        }
    });
}

function openAdd(parent) {
    resetForm({ parentId: parent.id || 0, sortOrder: 0 });
    dialogVisible.value = true;
}

function openEdit(row) {
    resetForm({ ...row, children: undefined });
    dialogVisible.value = true;
}

function syncReportPath() {
    if (form.menuType === "report" && form.reportId) {
        form.path = `/reportManage/view/${form.reportId}`;
        form.component = "ReportManage";
    }
}

function submitForm() {
    if (!form.title || !form.path) {
        ElMessage.warning("菜单名称和路由地址不能为空");
        return;
    }
    syncReportPath();
    const payload = { ...form };
    delete payload.children;
    saveMenuFun(payload).then((res) => {
        if (res.code === 200) {
            ElMessage.success("保存成功");
            dialogVisible.value = false;
            loadMenus();
        }
    });
}

function toggleStatus(row) {
    updateMenuStatusFun(row.id, row.status === 1 ? 0 : 1).then((res) => {
        if (res.code === 200) {
            ElMessage.success("状态已更新");
            loadMenus();
        }
    });
}

function removeMenu(row) {
    ElMessageBox.confirm(`确认删除菜单「${row.title}」？`, "提示", { type: "warning" }).then(() => {
        deleteMenuFun(row.id).then((res) => {
            if (res.code === 200) {
                ElMessage.success("删除成功");
                loadMenus();
            }
        });
    }).catch(() => {});
}

function menuTypeText(type) {
    return { catalog: "目录", page: "页面", report: "报表", button: "按钮" }[type] || type;
}

function menuTypeTag(type) {
    return { catalog: "info", page: "primary", report: "success", button: "warning" }[type] || "info";
}

loadMenus();
</script>

<style lang="scss" scoped>
.menu-page {
    padding: 16px;
}

.page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.page-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
}

.page-subtitle {
    margin-top: 4px;
    font-size: 12px;
    color: #8a94a6;
}

.status-switch {
    margin-left: 24px;
}
</style>